import React, { useImperativeHandle, useRef } from 'react';
import {
  Button,
  InputAccessoryView,
  Keyboard,
  StyleSheet,
  TextInput,
  TextInputProps,
  View,
} from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ExpressiveXXLText from 'shared/components/vxl/typography/expressive/ExpressiveXXLText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { isDesktopBrowser, isIOS, isWeb } from 'shared/constants/platform';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { applyKeyToAmount } from 'shared/utils/numberUtils';
import { formatCurrency } from 'shared/utils/string/currency';

export interface AmountInputProps
  extends TextInputProps, PressableAnalyticsProps {
  captionTextColor?: string;
  captionText?: string;
  captionTextIcon?: string;
  prefix?: string;
  suffix?: string;
  isDisabled?: boolean;
  formatAmount?: boolean;
  ref?: React.Ref<AmountInputRef>;
  showDoneButton?: boolean;
  /**
   * Computes the next amount for a key press. Returns undefined to ignore the
   * key. Defaults to applyKeyToAmount.
   */
  applyKey?: (amount: string, key: string) => string | undefined;
}

export interface AmountInputRef {
  focus: () => void;
  blur: () => void;
}

const MINIMUM_TOUCH_TARGET_SIZE = 48;

const AmountInput = ({
  value,
  analyticsId = '',
  customAttributes = null,
  onChangeText,
  onFocus,
  onBlur,
  captionText,
  captionTextIcon,
  captionTextColor,
  accessibilityLabel,
  prefix = '',
  suffix = '',
  isDisabled = false,
  formatAmount = false,
  autoFocus = isDesktopBrowser,
  showDoneButton = true,
  applyKey = applyKeyToAmount,
  ref,
}: AmountInputProps) => {
  const inputRef = useRef<TextInput>(null);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    blur: () => inputRef.current?.blur(),
  }));

  const textFieldAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes: { ...customAttributes },
  });

  const handleKeyPress = ({
    nativeEvent,
  }: {
    nativeEvent: { key: string };
  }) => {
    if (value) {
      const newAmount = applyKey(value, nativeEvent.key);
      if (newAmount !== undefined) {
        onChangeText?.(newAmount);
      }
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.amountTextWrapper}>
        <TextInput
          ref={inputRef}
          testID="amount-input-field"
          style={styles.hiddenInput}
          value={value}
          keyboardType="number-pad"
          onKeyPress={handleKeyPress}
          editable={!isDisabled}
          autoFocus={autoFocus}
          cursorColor="transparent"
          caretHidden
          inputAccessoryViewID={showDoneButton ? 'doneAccessory' : undefined}
          accessible
          accessibilityLabel={accessibilityLabel}
          accessibilityRole="adjustable"
          accessibilityHint="Enter dollar amount using keypad"
          accessibilityValue={{ text: `${prefix}${value}${suffix}` }}
          onFocus={(event) => {
            textFieldAnalytics();
            onFocus?.(event);
          }}
          onBlur={onBlur}
          disabled={isDisabled}
        />

        <ExpressiveXXLText
          numberOfLines={1}
          accessible={false}
          importantForAccessibility="no-hide-descendants"
        >
          {formatAmount ? formatCurrency(value) : `${prefix}${value}${suffix}`}
        </ExpressiveXXLText>
      </View>

      {captionText ? (
        <View style={styles.captionContainer}>
          {captionTextIcon ? (
            <Icon
              iconKey={captionTextIcon}
              iconSize="SM"
              iconColor={
                captionTextColor ? captionTextColor : theme.color.text.secondary
              }
            />
          ) : null}
          <BodyXSText
            style={[
              styles.captionText,
              {
                color: captionTextColor
                  ? captionTextColor
                  : theme.color.text.secondary,
              },
            ]}
            accessibilityRole="text"
          >
            {captionText}
          </BodyXSText>
        </View>
      ) : null}

      {isIOS && showDoneButton ? (
        <InputAccessoryView nativeID="doneAccessory">
          <View style={styles.accessory}>
            <Button
              onPress={() => Keyboard.dismiss()}
              title="Done"
              accessibilityLabel="Done entering amount"
            />
          </View>
        </InputAccessoryView>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  accessory: {
    alignItems: 'flex-end',
    backgroundColor: theme.color.background.surface,
    padding: theme.spacing.s,
  },
  amountTextWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: MINIMUM_TOUCH_TARGET_SIZE,
    minWidth: MINIMUM_TOUCH_TARGET_SIZE,
    width: '100%',
  },
  captionContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    marginTop: isWeb ? theme.spacing.m : theme.spacing.xs,
  },
  captionText: {
    flexShrink: 1,
  },
  container: {
    alignItems: 'center',
  },
  hiddenInput: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'transparent',
    color: 'transparent',
    fontSize: 1,
    opacity: 1,
    textAlign: 'center',
    zIndex: 1,
  },
});

export default AmountInput;
